/**
 * Retry policy and failure classes for the iOS sign-in handover: the page the
 * app opens carries a Firebase ID token in its URL, and `App.js` trades it for
 * a web session through `authService.verifyAndSignInWithToken`.
 *
 * Sign-in belongs to the app, so a handover that fails must never fall back to
 * a web sign-in form. It retries what a retry can fix, then shows an error the
 * user can act on (`IOSHandoverError`).
 *
 * Pure: imports nothing, so it tests without a Firebase app.
 */

export const HANDOVER_POLICY = Object.freeze({
  attempts: 3,
  // A cold start of the bridge function loads the whole functions codebase
  // before it answers: ~6 s of module loading on prod on 2026-09-21, on top of
  // container start-up and the phone's network. The old 8 s cutoff gave up
  // just as the token arrived.
  attemptTimeoutMs: 20000,
  // Wait before attempt 2 and before attempt 3.
  backoffMs: Object.freeze([1000, 2000]),
  // When "Authenticating..." becomes "Still connecting…", so a long first
  // attempt doesn't look frozen.
  slowNoticeMs: 8000,
});

/**
 * 'rejected' when the bridge refused the token itself (expired, revoked,
 * malformed, or not an account): the same token can never succeed, and only
 * the app can mint a new one. Anything else — a timeout, no network, a server
 * error — is 'retryable'.
 *
 * `status` is the HTTP status `verifyAndSignInWithToken` attaches to a failed
 * bridge response; the callable protocol maps invalid-argument, unauthenticated
 * and permission-denied to 400, 401 and 403.
 */
export function classifyHandoverError(error) {
  const status = error?.status;
  return status === 400 || status === 401 || status === 403
    ? 'rejected'
    : 'retryable';
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run `attempt()` up to `policy.attempts` times, stopping at the first success
 * or the first 'rejected' failure, waiting `policy.backoffMs[i]` between tries.
 * `onRetry(nextAttemptNumber, error)` fires before each wait. Rejects with the
 * last error; classify it with `classifyHandoverError`.
 */
export async function runHandover(
  attempt,
  { policy = HANDOVER_POLICY, sleep = wait, onRetry } = {}
) {
  for (let i = 0; ; i += 1) {
    try {
      return await attempt();
    } catch (error) {
      const isLast = i === policy.attempts - 1;
      if (isLast || classifyHandoverError(error) === 'rejected') throw error;
      onRetry?.(i + 2, error);
      await sleep(policy.backoffMs[i]);
    }
  }
}

/**
 * The report for a handover that SUCCEEDED slowly, or null when it was quick.
 * The failure path already reports itself (a console.error); a slow success
 * leaves nothing, and it is the case that looks like "it always says still
 * connecting…" from the phone. Slow means the user saw the slow notice, or it
 * took more than one attempt however quick.
 *
 * `retryErrors` are the errors that ended each failed attempt, oldest first.
 */
export function describeSlowHandover(
  { elapsedMs, attempts, retryErrors },
  policy = HANDOVER_POLICY
) {
  if (attempts <= 1 && elapsedMs < policy.slowNoticeMs) return null;
  const seconds = (elapsedMs / 1000).toFixed(1);
  return {
    message: `sign-in handover took ${seconds} s over ${attempts} attempt${
      attempts === 1 ? '' : 's'
    }`,
    context: {
      elapsedMs: Math.round(elapsedMs),
      attempts,
      retryErrors: retryErrors
        .map((error) => `${error?.name}: ${error?.message}`)
        .join(' | '),
    },
  };
}
